// The plan renderer, apart from `FloorPlanEditor.tsx` so the public Show map ships without the
// editor (TRO-1972). The editor draws its plan through this same component.
import { useEffect, useMemo, useRef, useState } from "react";
import { View } from "react-native";
import { Gesture, GestureDetector, PointerType } from "react-native-gesture-handler";
import Svg, {
  Circle,
  G,
  Image as SvgImage,
  Line,
  Path,
  Polygon,
  Polyline,
  Rect,
  Text as SvgText,
} from "react-native-svg";

import {
  envelopePolygon,
  type FloorPlanDocument,
  type FloorPlanPoint,
  type FloorPlanUnderlay,
  formatLength,
  placementIssues,
  SIXTEENTHS_PER_FOOT,
  tablePolygon,
  tablesBounds,
} from "@/lib/shows/floorPlan";
import { type FloorPlanOverlay, tableAt } from "@/lib/shows/floorPlanInteraction";
import {
  type CanvasSize,
  DEFAULT_WORLD,
  fitViewBox,
  screenToWorld,
  unitsPerPixel,
  type ViewBox,
} from "@/lib/shows/floorPlanViewport";
import { isRecord } from "@/lib/ui/validatedJson";
import { useThemeColors } from "@/theme/theme";

export function isMeasurable(value: unknown): value is HTMLElement {
  return isRecord(value) && typeof value.getBoundingClientRect === "function";
}

/** The size in pixels a canvas assumes until it has been laid out. */
const SEED_SIZE: CanvasSize = { width: 900, height: 620 };

/**
 * Returns the frame a canvas fits its plan into before it knows its own size.
 *
 * `onLayout` lands only after the first paint, and `getBoundingClientRect` is unavailable off web,
 * so the read-only show map paints its first frame with this. Framing on `SEED_SIZE` would
 * letterbox a square hall in a 1.45 box; using the plan's own aspect keeps it filling the frame.
 */
function contentFrame(content: ContentBounds | null): CanvasSize {
  const area = content && content.width > 0 && content.height > 0 ? content : DEFAULT_WORLD;
  return { width: SEED_SIZE.width, height: (SEED_SIZE.width * area.height) / area.width };
}

export function isWheelTarget(value: unknown): value is HTMLElement {
  return isMeasurable(value) && typeof value.addEventListener === "function";
}

export type ContentBounds = { x: number; y: number; width: number; height: number; };

const CURSOR_CLASS = {
  default: "cursor-default",
  crosshair: "cursor-crosshair",
  grab: "cursor-grab",
  grabbing: "cursor-grabbing",
  move: "cursor-move",
  // CSS ships no rotate cursor; `alias` is the nearest stock shape that is not already spoken for
  // here, and the transient arc at the corner carries the meaning.
  rotate: "cursor-alias",
} as const;

export const GRID = SIXTEENTHS_PER_FOOT;

/**
 * Lucide's rotate-ccw, drawn at world scale instead of in pixels. Hand-rolling the arc is what
 * produced a copyright mark twice over; the icon the rest of the product uses for "turn this"
 * already reads as turning, so the canvas borrows its geometry rather than approximating it.
 */
const ROTATE_GLYPH: readonly string[] = [
  "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8",
  "M3 3v5h5",
];

/**
 * Lucide draws on a 24-unit square; this puts that square on the canvas, centred, to size, and
 * turned with whatever it is attached to.
 */
function glyphTransform(center: FloorPlanPoint, size: number, angle: number): string {
  return `translate(${center.x} ${center.y}) rotate(${angle}) scale(${size / 24}) `
    + `translate(-12 -12)`;
}

/**
 * How an image is named in session state. Callers need not mint an id - the manage route adapts a
 * persisted underlay that has none - so fall back to what the document does carry.
 */
export function imageKey(image: FloorPlanUnderlay): string {
  return image.id ?? image.objectKey ?? image.uri;
}

/** Where a source image lands in world units, from its own calibration. */
export function underlayBox(image: FloorPlanUnderlay) {
  const { worldOrigin, sourceOrigin, sixteenthsPerSourceUnit } = image.calibration;
  return {
    x: worldOrigin.x - sourceOrigin.x * sixteenthsPerSourceUnit,
    y: worldOrigin.y - sourceOrigin.y * sixteenthsPerSourceUnit,
    width: image.sourceWidth * sixteenthsPerSourceUnit,
    height: image.sourceHeight * sixteenthsPerSourceUnit,
  };
}

/** A right-drag shorter than this was a click asking for the menu, not a grab of the floor. */
const RIGHT_DRAG_PIXELS = 5;

const points = (polygon: readonly FloorPlanPoint[]) =>
  polygon.map((point) => `${point.x},${point.y}`).join(" ");

export const clamp = (value: number, least: number, most: number) =>
  Math.min(Math.max(value, least), Math.max(least, most));

/** The world rectangle the plan actually occupies, or null while nothing has been drawn. */
export function contentBounds(plan: FloorPlanDocument): ContentBounds | null {
  const corners = [
    ...plan.boundary,
    ...plan.areas.flatMap((area) => area.polygon),
    ...plan.tables.flatMap(tablePolygon),
  ];
  if (!corners.length) return null;
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    x,
    y,
    width: Math.max(1, Math.max(...xs) - x),
    height: Math.max(1, Math.max(...ys) - y),
  };
}

export function centroid(polygon: readonly FloorPlanPoint[]): FloorPlanPoint {
  return polygon.reduce(
    (sum, point) => ({ x: sum.x + point.x / polygon.length, y: sum.y + point.y / polygon.length }),
    { x: 0, y: 0 },
  );
}

/** "40′ × 30′ · 1,200 sq ft" for the live room-drag readout. */
function describeRectangle(polygon: readonly FloorPlanPoint[]): string {
  const xs = polygon.map((point) => point.x);
  const ys = polygon.map((point) => point.y);
  const width = Math.max(...xs) - Math.min(...xs);
  const depth = Math.max(...ys) - Math.min(...ys);
  const squareFeet = Math.round((width * depth) / SIXTEENTHS_PER_FOOT ** 2);
  return `${formatLength(width)} × ${formatLength(depth)} · ${squareFeet.toLocaleString()} sq ft`;
}

/**
 * How far a finger has to travel before a held press counts as a deliberate one-finger drag,
 * in screen pixels. Gesture Handler's own touch slop, for the same reason it has one.
 */
const PRESS_SLOP = 15;

export type FloorPlanCanvasProps = {
  document: FloorPlanDocument;
  /** Background images, painted bottom-up by `layerIndex`. */
  underlays?: readonly FloorPlanUnderlay[];
  /** The image currently being worked on, outlined so it reads as the selected object. */
  selectedImageId?: string | null;
  selectedTableIds?: readonly string[];
  selectedAreaIds?: readonly string[];
  tableStates?: Readonly<Record<string, "unassigned" | "assigned" | "searchable">>;
  highlightedTableIds?: readonly string[];
  /** Transient authoring shapes from `floorPlanOverlay`; the public map passes none. */
  overlay?: FloorPlanOverlay | null;
  /** Controlled camera. When omitted, the canvas frames the whole plan, as the read-only public
   * map needs. */
  viewBox?: ViewBox;
  /** Reports the painted size so a controlling editor can compute its own camera. */
  onResize?: (size: CanvasSize) => void;
  /** Raw wheel/trackpad input in canvas coordinates; web only. */
  onWheel?: (wheel: CanvasWheel) => void;
  cursor?: "default" | "crosshair" | "grab" | "grabbing" | "move" | "rotate";
  onPointerDown?: (point: FloorPlanPoint) => void;
  onPointerMove?: (point: FloorPlanPoint) => void;
  onPointerUp?: (point: FloorPlanPoint) => void;
  /**
   * The press that began is not going to finish: the gesture turned out to be something else.
   * Every `onPointerDown` reaches exactly one of this or `onPointerUp`, so whatever the press
   * put in hand has somewhere to be put down.
   */
  onPointerCancel?: () => void;
  onTablePress?: (tableId: string) => void;
  /** Spacing and visibility of the drawing grid. Omit it and no grid is painted. */
  grid?: { size: number; visible: boolean; };
  /** Vendor artwork per table id, drawn overlapping the table's top edge. */
  tableBadges?: Readonly<Record<string, string>>;
  /** Vendor names per table id, drawn under the table where they cannot crowd the number. */
  tableCaptions?: Readonly<Record<string, string>>;
  /** Double-click anywhere; the editor decides from what is under the pointer. Web only. */
  onDoubleClick?: (point: FloorPlanPoint) => void;
  /** Right-click, with canvas-relative pixels for placing a menu. Web only. */
  onContextMenu?: (at: { x: number; y: number; }, point: FloorPlanPoint) => void;
  /** Files dropped onto the plan; web only. */
  onDropFiles?: (files: File[]) => void;
  /**
   * A right- or middle-button drag (web only, bound to the DOM node) or a two-finger drag (any
   * platform, through the gesture responder), in screen pixels, grab-and-drag sense.
   */
  onDragPan?: (screenDelta: { x: number; y: number; }) => void;
  /**
   * Where a hovering pointer is, in canvas pixels, and whether it is a fine one. Null when it
   * leaves. Web only, and deliberately NOT a React update: it fires every frame.
   */
  onHover?: (at: { x: number; y: number; } | null, fine: boolean) => void;
};

export type CanvasWheel = {
  /** Positive zooms in; zero when the gesture is a pan. */
  zoom: number;
  pan: { x: number; y: number; };
  at: { x: number; y: number; };
};

/** Draws a floor plan in native SVG; published plans render the same measured document read-only. */
export function FloorPlanCanvas({
  document: plan,
  underlays = [],
  selectedImageId = null,
  selectedTableIds = [],
  selectedAreaIds = [],
  tableStates,
  highlightedTableIds = [],
  overlay = null,
  viewBox,
  onResize,
  onWheel,
  cursor = "default",
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onTablePress,
  grid,
  tableBadges,
  tableCaptions,
  onDoubleClick,
  onContextMenu,
  onDropFiles,
  onDragPan,
  onHover,
}: FloorPlanCanvasProps) {
  const colors = useThemeColors();
  const [node, setNode] = useState<unknown>();
  const [measured, setMeasured] = useState<CanvasSize | null>(null);
  // Scans every corner of every area and table. Memoized because the plan does not change during
  // a drag or a pan, when this component re-renders most.
  const content = useMemo(() => contentBounds(plan), [plan]);
  // One size backs both the SVG box and the pixel scale that `screenToWorld` and the PDF underlay
  // divide by. Separate expressions would misplace the pointer on the first frame of any canvas
  // without its own camera.
  const size = measured ?? contentFrame(content);
  const view = viewBox ?? fitViewBox(content, size);
  const toWorld = ({ x, y }: { x: number; y: number; }) =>
    screenToWorld(
      { x, y },
      view,
      size,
    );
  const onWheelRef = useRef(onWheel);
  onWheelRef.current = onWheel;
  /**
   * Idempotent, so `onLayout` and the web ResizeObserver can both report the same box without
   * looping. `onLayout` works on every platform; the observer also catches the synchronous first
   * measurement, before layout has been committed.
   */
  const measuredRef = useRef<CanvasSize | null>(null);
  const measure = (next: CanvasSize) => {
    if (!next.width || !next.height) return;
    const current = measuredRef.current;
    if (current && current.width === next.width && current.height === next.height) return;
    measuredRef.current = next;
    setMeasured(next);
    onResize?.(next);
  };
  const measureRef = useRef(measure);
  measureRef.current = measure;
  // Drag and tap are separate gestures. Only the editor draws; the public show map passes only
  // `onTablePress`. A pan enabled on that map would claim every vertical drag over the canvas,
  // which sits inside the show page's scroller, so the page could not scroll from the plan. A tap
  // routed through a pan lands only if the pan becomes active, which a still finger never does.
  const dragging = !!(onPointerDown || onPointerMove || onPointerUp);
  const tappable = !!onTablePress;
  const panWithTouch = !!onDragPan;
  // New handlers must not rebuild the gesture: the editor re-renders on every pointer move with
  // fresh callbacks. Same idiom as `onWheelRef` above.
  const handlers = useRef({
    toWorld,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onTablePress,
    onDragPan,
    plan,
  });
  handlers.current = {
    toWorld,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onTablePress,
    onDragPan,
    plan,
  };
  /*
   * Touch has no middle or right button, so a second finger is the only way to pan without
   * leaving the drawing tool armed. Drawing and panning are two gestures rather than one that
   * works out mid-stream which job it is doing, because Gesture Handler decides the pointer
   * bands for us: `maxPointers(1)` makes it cancel the drawing pan outright the moment a second
   * finger lands, and `minPointers(2)` means the panning one cannot begin from a single finger
   * at all.
   *
   * What the bands cannot do is un-ring a bell. Fingers land one at a time, so the first of a
   * two-finger touch still begins the drawing pan, and a press is not free: `onPointerDown`
   * places a table, drops a carried image, or closes a trace outright. So on TOUCH the press is
   * held here until the gesture proves single-finger - it moved, or it ended - and a second
   * finger arriving first means it is simply never delivered. A mouse or stylus cannot grow a
   * second pointer, so it presses immediately and behaves exactly as it always has.
   *
   * One window is left open on purpose. A first finger that travels past `PRESS_SLOP` before its
   * partner lands releases the press, and a press that commits - a table, a closed trace, a
   * dropped image - is past anything `onPointerCancel` can take back. Reaching it takes a
   * deliberate couple of frames of travel, it cannot disturb what is already on the plan, and the
   * whole of the damage is one undo, which is cheaper than holding every press behind a timer.
   */
  const pendingPress = useRef<FloorPlanPoint | null>(null);
  const twoFingerFrom = useRef({ x: 0, y: 0 });
  const gesture = useMemo(() => {
    const pressNow = () => {
      const point = pendingPress.current;
      if (!point) return;
      pendingPress.current = null;
      handlers.current.onPointerDown?.(point);
    };
    return Gesture.Simultaneous(
      Gesture.Pan()
        .enabled(dragging)
        .minDistance(0)
        .maxPointers(1)
        .shouldCancelWhenOutside(false)
        .runOnJS(true)
        .onBegin((event) => {
          const point = handlers.current.toWorld(event);
          if (event.pointerType === PointerType.TOUCH) {
            pendingPress.current = point;
            return;
          }
          handlers.current.onPointerDown?.(point);
        })
        .onUpdate((event) => {
          // Two things have to be true before a held press is let go. The update must still
          // report one pointer, because a second finger is registered before Gesture Handler
          // gets round to cancelling this gesture. And the finger must have travelled far
          // enough to mean it: a contact flattening as it lands drifts a pixel or two on its
          // own, and releasing on that would place a table the pan is about to cancel. Below
          // the slop the move is swallowed whole, so nothing reaches the plan either.
          if ((event.numberOfPointers ?? 1) > 1) return;
          if (
            pendingPress.current
            && Math.hypot(event.translationX, event.translationY) < PRESS_SLOP
          ) {
            return;
          }
          pressNow();
          handlers.current.onPointerMove?.(handlers.current.toWorld(event));
        })
        .onEnd((event, success) => {
          // A cancelled pan reaches `onEnd` too, carrying `success: false`. Leaving the press
          // held there is what lets `onFinalize` below see it was never delivered.
          if (!success) return;
          pressNow();
          handlers.current.onPointerUp?.(handlers.current.toWorld(event));
        })
        .onFinalize((_event, success) => {
          // A press still waiting was never delivered, so there is nothing to take back.
          const waiting = pendingPress.current !== null;
          pendingPress.current = null;
          if (!success && !waiting) handlers.current.onPointerCancel?.();
        })
        .withTestId("floor-plan-canvas-pan"),
      Gesture.Pan()
        .enabled(panWithTouch)
        .minDistance(0)
        .minPointers(2)
        .maxPointers(2)
        .shouldCancelWhenOutside(false)
        .runOnJS(true)
        .onBegin((event) => {
          // Where this pan starts from, so the first frame reports the fingers' own travel
          // rather than whatever the first finger had already covered alone.
          twoFingerFrom.current = { x: event.translationX, y: event.translationY };
        })
        .onUpdate((event) => {
          const from = twoFingerFrom.current;
          handlers.current.onDragPan?.({
            x: from.x - event.translationX,
            y: from.y - event.translationY,
          });
          twoFingerFrom.current = { x: event.translationX, y: event.translationY };
        })
        .withTestId("floor-plan-canvas-two-finger-pan"),
      Gesture.Tap()
        .enabled(tappable)
        .runOnJS(true)
        .onEnd((event) => {
          const current = handlers.current;
          const table = tableAt(current.plan, current.toWorld(event));
          if (table) current.onTablePress?.(table.id);
        })
        .withTestId("floor-plan-canvas-tap"),
    );
  }, [dragging, tappable, panWithTouch]);
  const publicMode = tableStates !== undefined;
  const highlighted = useMemo(() => new Set(highlightedTableIds), [highlightedTableIds]);
  const selectedIds = useMemo(() => new Set(selectedTableIds), [selectedTableIds]);
  const selectedAreas = useMemo(() => new Set(selectedAreaIds), [selectedAreaIds]);
  const stroke = Math.max(12, view.width / 700);
  /*
   * World units per screen pixel. Content is measured in feet and scales with the plan; the
   * controls hanging off it are measured HERE, in pixels, because they are aimed at rather than
   * read. `stroke` is a fraction of the viewBox, so sizing a control with it made the whole
   * control shrink with the pane — a plus-arrow a pixel wide on a narrow canvas.
   */
  const pixel = unitsPerPixel(view, size);

  useEffect(() => {
    if (!isMeasurable(node)) return undefined;
    const element = node;
    const remeasure = () => {
      const rect = element.getBoundingClientRect();
      measureRef.current({ width: rect.width, height: rect.height });
    };
    remeasure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(remeasure);
    observer.observe(element);
    return () => observer.disconnect();
    // `measureRef` is refreshed every render, and the measurement depends only on the node, so
    // re-subscribing on every render would only churn the observer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node]);

  /**
   * Wheel input never reaches React Native's responder system, so it is bound to the DOM node.
   * `passive: false` lets the listener suppress browser page zoom and rubber-band scroll while the
   * pointer is over the plan.
   */
  useEffect(() => {
    if (!onWheel || !isWheelTarget(node)) return undefined;
    const element = node;
    const handle = (event: WheelEvent) => {
      event.preventDefault();
      const rect = element.getBoundingClientRect();
      const at = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      /*
       * The wheel zooms, anchored under the cursor, which is what a map wants and what a mouse
       * has no other way to ask for. Horizontal delta still pans, so a trackpad swipe sideways
       * and shift+wheel both keep working; vertical panning belongs to middle-drag and
       * space-drag, which cost nothing to reach.
       */
      onWheelRef.current?.({
        zoom: -event.deltaY,
        pan: { x: event.deltaX, y: 0 },
        at,
      });
    };
    element.addEventListener("wheel", handle, { passive: false });
    return () => element.removeEventListener("wheel", handle);
    // The handler reads `onWheelRef`, so only whether a handler exists affects the subscription;
    // depending on the callback itself would rebind every frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, !!onWheel]);

  const hoverRef = useRef(onHover);
  hoverRef.current = onHover;

  /**
   * Hover, bound to the DOM because the responder system does not report a pointer that is not
   * pressed. Reported straight back out rather than through state: this fires on every frame a
   * mouse moves, and a re-render per frame over a full hall is not worth a cursor badge.
   */
  useEffect(() => {
    if (!onHover || !isWheelTarget(node)) return undefined;
    const element = node;
    const moved = (event: PointerEvent) => {
      const rect = element.getBoundingClientRect();
      hoverRef.current?.(
        { x: event.clientX - rect.left, y: event.clientY - rect.top },
        event.pointerType === "mouse",
      );
    };
    const gone = () => hoverRef.current?.(null, false);
    element.addEventListener("pointermove", moved);
    element.addEventListener("pointerleave", gone);
    return () => {
      element.removeEventListener("pointermove", moved);
      element.removeEventListener("pointerleave", gone);
    };
    // Same as wheel: only whether a handler exists can change the subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, !!onHover]);

  const cornersRef = useRef({ onDoubleClick, onContextMenu, toWorld });
  cornersRef.current = { onDoubleClick, onContextMenu, toWorld };

  /**
   * Neither double-click nor right-click reaches the responder system, so both are bound to the
   * DOM node the way wheel is. Suppressing the browser menu is the point of the second one.
   */
  useEffect(() => {
    if (!isWheelTarget(node) || (!onDoubleClick && !onContextMenu)) return undefined;
    const element = node;
    const at = (event: MouseEvent) => {
      const rect = element.getBoundingClientRect();
      return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };
    /*
     * The browser menu is always suppressed, but ours opens on RELEASE rather than here: the
     * `contextmenu` event fires on press on macOS and on release elsewhere, and a right-drag that
     * pans has to be able to outrun it either way. Owning the menu means owning when it opens.
     */
    const suppressed = (event: MouseEvent) => event.preventDefault();
    const released = (event: MouseEvent) => {
      if (event.button !== 2) return;
      const travelled = rightTravel.current;
      rightTravel.current = null;
      // A right-press that moved was a grab of the floor, not a request for a menu.
      if (travelled !== null && travelled > RIGHT_DRAG_PIXELS) return;
      const where = at(event);
      cornersRef.current.onContextMenu?.(where, cornersRef.current.toWorld(where));
    };
    const doubled = (event: MouseEvent) => {
      event.preventDefault();
      cornersRef.current.onDoubleClick?.(cornersRef.current.toWorld(at(event)));
    };
    element.addEventListener("dblclick", doubled);
    element.addEventListener("contextmenu", suppressed);
    element.addEventListener("mouseup", released);
    return () => {
      element.removeEventListener("dblclick", doubled);
      element.removeEventListener("contextmenu", suppressed);
      element.removeEventListener("mouseup", released);
    };
    // Same idiom as wheel: the handlers are read through a ref, so only their existence matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, !!onDoubleClick, !!onContextMenu]);

  const dropRef = useRef(onDropFiles);
  dropRef.current = onDropFiles;

  /** Dropping a venue photo straight onto the floor is the shortest path to a background. */
  useEffect(() => {
    if (!isWheelTarget(node) || !onDropFiles) return undefined;
    const element = node;
    const over = (event: DragEvent) => event.preventDefault();
    const dropped = (event: DragEvent) => {
      event.preventDefault();
      const files = [...(event.dataTransfer?.files ?? [])];
      if (files.length) dropRef.current?.(files);
    };
    element.addEventListener("dragover", over);
    element.addEventListener("drop", dropped);
    return () => {
      element.removeEventListener("dragover", over);
      element.removeEventListener("drop", dropped);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, !!onDropFiles]);

  const dragPanRef = useRef(onDragPan);
  dragPanRef.current = onDragPan;
  /*
   * How far the current right-drag has travelled, in pixels. Shared with the menu below, which is
   * how a right-press that PANNED is told apart from one that asked for a menu.
   */
  const rightTravel = useRef<number | null>(null);

  /**
   * Right- and middle-drag both pan, from anywhere, without disturbing the selection or the armed
   * tool — which is what makes them worth having alongside space-drag: neither needs a free hand.
   * Move and release are bound to the window so a drag that leaves the canvas still tracks and
   * still ends. Preventing the default mousedown suppresses the browser's middle-click autoscroll.
   */
  useEffect(() => {
    if (!isWheelTarget(node) || !onDragPan) return undefined;
    const element = node;
    let last: { x: number; y: number; } | null = null;
    const down = (event: MouseEvent) => {
      if (event.button !== 1 && event.button !== 2) return;
      if (event.button === 1) event.preventDefault();
      if (event.button === 2) rightTravel.current = 0;
      last = { x: event.clientX, y: event.clientY };
    };
    const move = (event: MouseEvent) => {
      if (!last) return;
      event.preventDefault();
      const delta = { x: last.x - event.clientX, y: last.y - event.clientY };
      if (rightTravel.current !== null) {
        rightTravel.current += Math.abs(delta.x) + Math.abs(delta.y);
      }
      dragPanRef.current?.(delta);
      last = { x: event.clientX, y: event.clientY };
    };
    const up = () => {
      last = null;
    };
    element.addEventListener("mousedown", down);
    globalThis.addEventListener("mousemove", move);
    globalThis.addEventListener("mouseup", up);
    return () => {
      element.removeEventListener("mousedown", down);
      globalThis.removeEventListener("mousemove", move);
      globalThis.removeEventListener("mouseup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, !!onDragPan]);

  /*
   * Drawn across the camera rather than inside the hall, because it is the ground you draw ON and
   * has to exist before a hall does. The pixel floor doubles as the zoom-out cutoff: past it the
   * lines would be denser than the screen can separate, and painting thousands of them costs a
   * frame to produce a grey wash.
   */
  const gridLines: React.ReactNode[] = [];
  const gridSize = grid?.size ?? GRID;
  if (grid?.visible && !publicMode && gridSize / unitsPerPixel(view, size) >= 6) {
    const right = view.x + view.width;
    const bottom = view.y + view.height;
    for (let x = Math.ceil(view.x / gridSize) * gridSize; x <= right; x += gridSize) {
      gridLines.push(<Line key={`x-${x}`} x1={x} y1={view.y} x2={x} y2={bottom} />);
    }
    for (let y = Math.ceil(view.y / gridSize) * gridSize; y <= bottom; y += gridSize) {
      gridLines.push(<Line key={`y-${y}`} x1={view.x} y1={y} x2={right} y2={y} />);
    }
  }

  /** Bottom-up, so a later image composes over an earlier one. */
  const ordered = [...underlays].sort((a, b) => (a.layerIndex ?? 0) - (b.layerIndex ?? 0));

  const canvas = (
    <View
      ref={(value) => setNode(value)}
      // `select-none` stops a drag from text-selecting every table number it crosses. It has no
      // effect off web, where there is no text selection.
      className={`relative h-full min-h-[420px] flex-1 select-none overflow-hidden bg-bg ${
        onTablePress ? "cursor-pointer" : CURSOR_CLASS[cursor]
      }`}
      accessibilityRole="image"
      accessibilityLabel={`Measured floor plan with ${plan.tables.length} tables and ${plan.areas.length} labelled areas`}
      accessibilityHint={onTablePress
        ? "Tap an assigned table to identify its Vendor"
        : undefined}
      onLayout={(event) => measure(event.nativeEvent.layout)}
    >
      <Svg
        width="100%"
        height="100%"
        viewBox={`${view.x} ${view.y} ${view.width} ${view.height}`}
        pointerEvents="none"
      >
        <Rect x={view.x} y={view.y} width={view.width} height={view.height} fill="transparent" />
        <G stroke={colors.border} strokeWidth={stroke / 2} opacity={0.55}>
          {gridLines}
        </G>
        {ordered.map((image) => {
          if (image.mediaType === "application/pdf") return null;
          const box = underlayBox(image);
          return (
            <SvgImage
              key={imageKey(image)}
              href={image.uri}
              x={box.x}
              y={box.y}
              width={box.width}
              height={box.height}
              opacity={image.opacity ?? 0.45}
              preserveAspectRatio="none"
            />
          );
        })}
        {ordered
          .filter((image) => imageKey(image) === selectedImageId)
          .map((image) => {
            const box = underlayBox(image);
            return (
              <Rect
                key={`chosen-${imageKey(image)}`}
                x={box.x}
                y={box.y}
                width={box.width}
                height={box.height}
                fill="none"
                stroke={colors.primary}
                strokeWidth={stroke * 2}
                strokeDasharray={`${stroke * 6} ${stroke * 4}`}
              />
            );
          })}
        {plan.boundary.length >= 3
          ? (
            <>
              <Polygon
                points={points(plan.boundary)}
                fill={colors.surface}
                fillOpacity={ordered.length ? 0.82 : 1}
                stroke={colors.foreground}
                strokeWidth={stroke * 2}
                strokeLinejoin="round"
              />
            </>
          )
          : null}
        {plan.areas.map((area) => {
          const label = centroid(area.polygon);
          const picked = selectedAreas.has(area.id);
          const hovered = overlay?.hoverAreaId === area.id;
          return (
            <G key={area.id}>
              <Polygon
                points={points(area.polygon)}
                fill={area.obstructs ? colors.loss : colors.primary}
                fillOpacity={picked ? 0.3 : area.obstructs ? 0.18 : 0.12}
                stroke={picked || hovered
                  ? colors.primary
                  : area.obstructs
                  ? colors.loss
                  : colors.primary}
                strokeWidth={picked ? stroke * 3 : hovered ? stroke * 2 : stroke}
              />
              {
                /* An area's name carries the same weight as a table's number, and the same
                ground-coloured halo under it, because both have to be readable across a hall
                and over whatever venue photo is underneath. */
              }
              <SvgText
                x={label.x}
                y={label.y + stroke * 6}
                fill="none"
                stroke={colors.surface}
                strokeWidth={stroke * 3}
                strokeLinejoin="round"
                fontFamily="BricolageGrotesque_600SemiBold"
                fontSize={stroke * 16}
                textAnchor="middle"
              >
                {area.name}
              </SvgText>
              <SvgText
                x={label.x}
                y={label.y + stroke * 6}
                fill={colors.foreground}
                fontFamily="BricolageGrotesque_600SemiBold"
                fontSize={stroke * 16}
                textAnchor="middle"
              >
                {area.name}
              </SvgText>
            </G>
          );
        })}
        {plan.tables.map((table) => {
          const illegal = !publicMode && placementIssues(table, plan).length > 0;
          const selected = selectedIds.has(table.id);
          const hovered = overlay?.hoverTableId === table.id;
          const tableState = tableStates?.[table.id] ?? "unassigned";
          const isHighlighted = highlighted.has(table.id);
          const tablePoints = tablePolygon(table);
          // Who holds it is a property of the table, so the table carries it. A name is only
          // legible up close; a wash of colour reads at any zoom and never crosses a neighbour.
          const held = !publicMode && !!tableCaptions?.[table.id];
          return (
            <G
              key={table.id}
              testID={publicMode ? "show-table" : undefined}
              accessibilityLabel={publicMode ? `Table ${table.number}` : undefined}
            >
              {!publicMode
                ? (
                  <Polygon
                    points={points(envelopePolygon(table, plan))}
                    fill="none"
                    stroke={illegal ? colors.loss : colors.subtle}
                    strokeDasharray={`${stroke * 3} ${stroke * 2}`}
                    strokeWidth={stroke / 1.5}
                  />
                )
                : null}
              <Polygon
                points={points(tablePoints)}
                fill={publicMode
                  ? colors.bg
                  : illegal
                  ? colors.loss
                  : selected || held
                  ? colors.primary
                  : colors.bg}
                fillOpacity={illegal ? 0.22 : held && !selected ? 0.2 : 1}
                stroke={illegal || selected || isHighlighted || hovered
                  ? illegal ? colors.loss : colors.primary
                  : held
                  ? colors.primary
                  : tableState === "unassigned"
                  ? colors.subtle
                  : colors.foreground}
                strokeWidth={stroke
                  * (isHighlighted
                    ? 4
                    : tableState === "searchable"
                    ? 3
                    : tableState === "assigned" || selected || held
                    ? 2
                    : 1)}
              />
              {illegal
                ? (
                  <G stroke={colors.loss} strokeWidth={stroke * 1.5}>
                    <Line
                      x1={table.center.x - stroke * 5}
                      y1={table.center.y - stroke * 5}
                      x2={table.center.x + stroke * 5}
                      y2={table.center.y + stroke * 5}
                    />
                    <Line
                      x1={table.center.x + stroke * 5}
                      y1={table.center.y - stroke * 5}
                      x2={table.center.x - stroke * 5}
                      y2={table.center.y + stroke * 5}
                    />
                  </G>
                )
                : null}
              {
                /* Drawn twice: a ground-coloured halo first so the number survives a busy
                venue photo underneath it, then the number itself. */
              }
              <SvgText
                x={table.center.x}
                y={table.center.y + stroke * 6}
                fill="none"
                stroke={colors.surface}
                strokeWidth={stroke * 3}
                strokeLinejoin="round"
                fontFamily="BricolageGrotesque_600SemiBold"
                fontSize={stroke * 16}
                textAnchor="middle"
              >
                {table.number}
              </SvgText>
              <SvgText
                x={table.center.x}
                y={table.center.y + stroke * 6}
                fill={colors.foreground}
                fontFamily="BricolageGrotesque_600SemiBold"
                fontSize={stroke * 16}
                textAnchor="middle"
              >
                {table.number}
              </SvgText>
              {held
                ? (() => {
                  /*
                   * Beside the number, because that is where the eye already is, and because a
                   * wash of colour on its own is not a signal everyone can see. Upright like the
                   * number it sits next to: a rotated table still has to be read.
                   */
                  const digits = stroke * 16;
                  const tick = stroke * 6;
                  const at = {
                    x: table.center.x + table.number.length * digits * 0.3 + tick * 0.7,
                    y: table.center.y,
                  };
                  const mark = [
                    `${at.x - tick * 0.45},${at.y + tick * 0.05}`,
                    `${at.x - tick * 0.12},${at.y + tick * 0.38}`,
                    `${at.x + tick * 0.45},${at.y - tick * 0.38}`,
                  ].join(" ");
                  return (
                    <G
                      testID="floor-plan-assigned"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <Polyline points={mark} stroke={colors.surface} strokeWidth={tick * 0.62} />
                      <Polyline points={mark} stroke={colors.primary} strokeWidth={tick * 0.3} />
                    </G>
                  );
                })()
                : null}
              {(() => {
                const name = tableCaptions?.[table.id];
                if (!name) return null;
                /*
                 * Only while it fits under its own table. Zoomed out to a whole hall a vendor
                 * name is wider than the table it belongs to, and it ran straight through the
                 * neighbours - so the wash of colour says WHETHER a table is spoken for at every
                 * zoom, and the name says WHO once there is room to read it.
                 */
                const face = stroke * 7;
                if (name.length * face * 0.55 > table.width) return null;
                // Under the footprint, not in it: the number is the figure that has to carry at
                // hall scale, and a name inside the table is what takes that away from it.
                const radians = (table.rotation * Math.PI) / 180;
                const reach = table.depth / 2 + face;
                return (
                  <SvgText
                    x={table.center.x - reach * Math.sin(radians)}
                    y={table.center.y + reach * Math.cos(radians)}
                    fill={colors.mutedForeground}
                    fontFamily="BricolageGrotesque_600SemiBold"
                    fontSize={face}
                    textAnchor="middle"
                  >
                    {name}
                  </SvgText>
                );
              })()}
              {(() => {
                const art = tableBadges?.[table.id];
                if (!art) return null;
                const radians = (table.rotation * Math.PI) / 180;
                const reach = table.depth / 2;
                const at = {
                  x: table.center.x + reach * Math.sin(radians),
                  y: table.center.y - reach * Math.cos(radians),
                };
                const badge = stroke * 9;
                return (
                  <G key={`badge-${table.id}`}>
                    <Circle
                      cx={at.x}
                      cy={at.y}
                      r={badge / 2 + stroke * 0.6}
                      fill={colors.surface}
                      stroke={colors.border}
                      strokeWidth={stroke}
                    />
                    <SvgImage
                      href={art}
                      x={at.x - badge / 2}
                      y={at.y - badge / 2}
                      width={badge}
                      height={badge}
                      preserveAspectRatio="xMidYMid slice"
                    />
                  </G>
                );
              })()}
            </G>
          );
        })}
        {overlay
          ? (
            <G>
              {overlay.boundaryPreview
                ? (
                  <Polygon
                    points={points(overlay.boundaryPreview)}
                    fill={colors.primary}
                    fillOpacity={0.08}
                    stroke={colors.primary}
                    strokeWidth={stroke * 2}
                    strokeDasharray={`${stroke * 5} ${stroke * 3}`}
                  />
                )
                : null}
              {overlay.roomPreview?.length
                ? (
                  <>
                    <Polygon
                      points={points(overlay.roomPreview)}
                      fill={colors.primary}
                      fillOpacity={0.08}
                      stroke={colors.primary}
                      strokeWidth={stroke * 2}
                      strokeDasharray={`${stroke * 5} ${stroke * 3}`}
                    />
                    <SvgText
                      x={centroid(overlay.roomPreview).x}
                      y={centroid(overlay.roomPreview).y}
                      fill={colors.primary}
                      fontFamily="BricolageGrotesque_600SemiBold"
                      fontSize={stroke * 10}
                      textAnchor="middle"
                    >
                      {describeRectangle(overlay.roomPreview)}
                    </SvgText>
                  </>
                )
                : null}
              {overlay.areaPreview?.length
                ? (
                  <Polygon
                    points={points(overlay.areaPreview)}
                    fill={colors.primary}
                    fillOpacity={0.16}
                    stroke={colors.primary}
                    strokeWidth={stroke * 2}
                    strokeDasharray={`${stroke * 5} ${stroke * 3}`}
                  />
                )
                : null}
              {overlay.marquee?.length
                ? (
                  <Polygon
                    points={points(overlay.marquee)}
                    fill={colors.primary}
                    fillOpacity={0.1}
                    stroke={colors.primary}
                    strokeWidth={stroke}
                    strokeDasharray={`${stroke * 3} ${stroke * 2}`}
                  />
                )
                : null}
              {
                /* A trace says three things at once: what is already committed (solid), where
                the next edge would run (dashed, following the pointer), and how to get out of
                it (a quiet dashed line home to the first corner). The way out matters most on
                touch, where there is no hover to discover it with. */
              }
              {overlay.draft.length > 0
                ? (
                  <G>
                    {overlay.draftClosing
                      ? (
                        <Polygon
                          points={points(overlay.draftClosing)}
                          fill={colors.primary}
                          fillOpacity={0.12}
                          stroke="none"
                        />
                      )
                      : null}
                    {overlay.draftGuide
                      ? (
                        <Line
                          x1={overlay.draftGuide[0].x}
                          y1={overlay.draftGuide[0].y}
                          x2={overlay.draftGuide[1].x}
                          y2={overlay.draftGuide[1].y}
                          stroke={colors.mutedForeground}
                          strokeWidth={stroke}
                          strokeDasharray={`${stroke * 2} ${stroke * 4}`}
                        />
                      )
                      : null}
                    {overlay.draftRubber
                      ? (
                        <Line
                          x1={overlay.draftRubber[0].x}
                          y1={overlay.draftRubber[0].y}
                          x2={overlay.draftRubber[1].x}
                          y2={overlay.draftRubber[1].y}
                          stroke={colors.primary}
                          strokeOpacity={0.6}
                          strokeWidth={stroke * 1.5}
                          strokeDasharray={`${stroke * 4} ${stroke * 3}`}
                        />
                      )
                      : null}
                    <Polyline
                      points={points(overlay.draft)}
                      fill="none"
                      stroke={colors.primary}
                      strokeWidth={stroke * 1.5}
                    />
                    {overlay.draft.map((point, index) => (
                      <Circle
                        key={index}
                        cx={point.x}
                        cy={point.y}
                        r={stroke * 2.5}
                        fill={colors.primary}
                      />
                    ))}
                    {/* The first corner grows a ring the moment a click there would close. */}
                    {overlay.draftClosing
                      ? (
                        <Circle
                          testID="floor-plan-close-trace"
                          cx={overlay.draft[0].x}
                          cy={overlay.draft[0].y}
                          r={stroke * 5}
                          fill="none"
                          stroke={colors.primary}
                          strokeWidth={stroke * 1.5}
                        />
                      )
                      : null}
                  </G>
                )
                : null}
              {overlay.repeating?.copies
                ? (() => {
                  const tiling = overlay.repeating;
                  const unit = plan.tables.filter((table) =>
                    tiling.selection.tables.includes(table.id)
                  );
                  // Signed, so a row marching back against the axis previews where it will
                  // actually land rather than mirrored onto the other side.
                  const step = tiling.pitch * tiling.direction;
                  return (
                    <G>
                      {Array.from({ length: tiling.copies }, (_, copy) =>
                        unit.map((table) => (
                          <Polygon
                            key={`ghost-${copy}-${table.id}`}
                            points={points(tablePolygon({
                              ...table,
                              center: {
                                x: table.center.x
                                  + (tiling.axis === "x" ? step * (copy + 1) : 0),
                                y: table.center.y
                                  + (tiling.axis === "y" ? step * (copy + 1) : 0),
                              },
                            }))}
                            fill={colors.primary}
                            fillOpacity={0.12}
                            stroke={colors.primary}
                            strokeWidth={stroke * 1.5}
                            strokeDasharray={`${stroke * 4} ${stroke * 2}`}
                          />
                        )))}
                    </G>
                  );
                })()
                : null}
              {
                /* The gap itself, measured: a double-headed arrow spanning the empty floor
                between the unit and the first copy, with the distance it stands for labelled at
                its middle - the number the drag is actually setting, read off the row rather than
                off a panel elsewhere. */
              }
              {overlay.repeating?.copies
                ? (() => {
                  const tiling = overlay.repeating;
                  const bounds = tablesBounds(plan, tiling.selection.tables);
                  const gap = Math.max(0, tiling.pitch - tiling.extent);
                  if (!bounds || gap <= 0) return null;
                  const nearEdge = tiling.axis === "x"
                    ? (tiling.direction === 1 ? bounds.x + bounds.width : bounds.x)
                    : (tiling.direction === 1 ? bounds.y + bounds.height : bounds.y);
                  const farEdge = nearEdge + gap * tiling.direction;
                  const across = tiling.axis === "x"
                    ? bounds.y + bounds.height / 2
                    : bounds.x + bounds.width / 2;
                  const from: FloorPlanPoint = tiling.axis === "x"
                    ? { x: nearEdge, y: across }
                    : { x: across, y: nearEdge };
                  const to: FloorPlanPoint = tiling.axis === "x"
                    ? { x: farEdge, y: across }
                    : { x: across, y: farEdge };
                  const wing = pixel * 3.2;
                  const arrowhead = (tip: FloorPlanPoint, from2: FloorPlanPoint) => {
                    const dx = tip.x - from2.x;
                    const dy = tip.y - from2.y;
                    const length = Math.hypot(dx, dy) || 1;
                    const ux = dx / length;
                    const uy = dy / length;
                    const backX = tip.x - ux * wing * 1.6;
                    const backY = tip.y - uy * wing * 1.6;
                    return `${tip.x},${tip.y} ${backX - uy * wing},${backY + ux * wing} ${
                      backX + uy * wing
                    },${backY - ux * wing}`;
                  };
                  const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
                  const label = formatLength(gap);
                  const width = stroke * (10 + label.length * 5.5);
                  const height = stroke * 13;
                  return (
                    <G>
                      <Line
                        x1={from.x}
                        y1={from.y}
                        x2={to.x}
                        y2={to.y}
                        stroke={colors.foreground}
                        strokeWidth={stroke}
                      />
                      <Polygon points={arrowhead(from, to)} fill={colors.foreground} />
                      <Polygon points={arrowhead(to, from)} fill={colors.foreground} />
                      <Rect
                        x={mid.x - width / 2}
                        y={mid.y - height / 2}
                        width={width}
                        height={height}
                        rx={height / 2}
                        fill={colors.foreground}
                      />
                      <SvgText
                        x={mid.x}
                        y={mid.y + stroke * 4}
                        fill={colors.surface}
                        fontFamily="BricolageGrotesque_600SemiBold"
                        fontSize={stroke * 11}
                        textAnchor="middle"
                      >
                        {label}
                      </SvgText>
                    </G>
                  );
                })()
                : null}
              {
                /* Two handles, off the right and bottom of whatever is picked. A plus beside an
                arrow rather than an arrow alone: a bare arrow on a table reads as a link to
                somewhere, and what this does is make more of the thing. */
              }
              {overlay.repeatControls
                ? (["x", "y"] as const).map((axis) => {
                  const at = overlay.repeatControls?.[axis];
                  if (!at) return null;
                  const tint = overlay.hoverRepeatAxis === axis
                    ? colors.primary
                    : colors.foreground;
                  const r = pixel * 11;
                  const arm = pixel * 3.5;
                  const shift = axis === "x" ? { x: 1, y: 0 } : { x: 0, y: 1 };
                  const tip = {
                    x: at.x + r * 0.5 * shift.x,
                    y: at.y + r * 0.5 * shift.y,
                  };
                  const wing = pixel * 3.6;
                  const head = axis === "x"
                    ? `${tip.x + wing},${tip.y} ${tip.x - wing * 0.6},${tip.y - wing} ${
                      tip.x - wing * 0.6
                    },${tip.y + wing}`
                    : `${tip.x},${tip.y + wing} ${tip.x - wing},${tip.y - wing * 0.6} ${
                      tip.x + wing
                    },${tip.y - wing * 0.6}`;
                  const plus = axis === "x"
                    ? { x: at.x - r * 0.45, y: at.y }
                    : { x: at.x, y: at.y - r * 0.45 };
                  return (
                    <G
                      key={`repeat-${axis}`}
                      testID={`floor-plan-repeat-${axis}`}
                      aria-label={axis === "x" ? "Repeat horizontally" : "Repeat vertically"}
                    >
                      {
                        /* A hairline border as well as a ground: on a pale venue photo a white
                        disc with no edge is not a control, it is a smudge. */
                      }
                      <Circle
                        cx={at.x}
                        cy={at.y}
                        r={r}
                        fill={colors.surface}
                        stroke={colors.border}
                        strokeWidth={pixel}
                      />
                      <G stroke={tint} strokeWidth={pixel * 1.75} strokeLinecap="round">
                        <Line
                          x1={plus.x - arm}
                          y1={plus.y}
                          x2={plus.x + arm}
                          y2={plus.y}
                        />
                        <Line
                          x1={plus.x}
                          y1={plus.y - arm}
                          x2={plus.x}
                          y2={plus.y + arm}
                        />
                      </G>
                      <Polygon points={head} fill={tint} />
                    </G>
                  );
                })
                : null}
              {
                /* One control, off the table's upper-right corner, for a single unlocked pick.
                It rides the table: the same corner as the table turns, and the icon turns with
                it, so the thing being dragged and the thing it turns stay one object. */
              }
              {overlay.rotateControl
                ? (() => {
                  const control = overlay.rotateControl;
                  const tint = overlay.hoverRotateTableId ? colors.primary : colors.foreground;
                  return (
                    <G>
                      {
                        /* Ground, not a border: the icon has to read on any venue photo, and a
                        ringed disc is what made this look like a copyright mark. */
                      }
                      <Circle
                        cx={control.at.x}
                        cy={control.at.y}
                        r={pixel * 11}
                        fill={colors.surface}
                        stroke={colors.border}
                        strokeWidth={pixel}
                      />
                      <G
                        transform={glyphTransform(control.at, pixel * 15, control.angle)}
                        fill="none"
                        stroke={tint}
                        strokeWidth={2.6}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {ROTATE_GLYPH.map((d) => <Path key={d} d={d} />)}
                      </G>
                    </G>
                  );
                })()
                : null}
              {
                /* Four handles, one per corner - a corner touches both edges, so dragging any one
                resizes both dimensions together. Sitting exactly on the table's own corners,
                unlike the rotate and repeat controls' standoff, which is what tells the three
                apart when they would otherwise land on top of each other for an unrotated table.
                Snapped to the foot as they drag (`snappedSpan`), so what is seen dragging is what
                gets committed on release. */
              }
              {overlay.resizeControls
                ? overlay.resizeControls.map((at, index) => {
                  const tint = overlay.hoverResizeCorner === index
                    ? colors.primary
                    : colors.foreground;
                  return (
                    <Circle
                      key={`resize-${index}`}
                      testID={`floor-plan-resize-corner-${index}`}
                      cx={at.x}
                      cy={at.y}
                      r={pixel * 7}
                      fill={tint}
                      stroke={colors.surface}
                      strokeWidth={pixel * 2}
                    />
                  );
                })
                : null}
              {
                /* One handle per corner of the selected area's own polygon - free-form, unlike a
                table: no fixed count, no foot snapping, only a nearby room edge pulls it. */
              }
              {overlay.areaResizeControls
                ? overlay.areaResizeControls.map((at, index) => {
                  const tint = overlay.hoverAreaVertexIndex === index
                    ? colors.primary
                    : colors.foreground;
                  return (
                    <Circle
                      key={`area-vertex-${index}`}
                      testID={`floor-plan-area-vertex-${index}`}
                      cx={at.x}
                      cy={at.y}
                      r={pixel * 7}
                      fill={tint}
                      stroke={colors.surface}
                      strokeWidth={pixel * 2}
                    />
                  );
                })
                : null}
              {
                /* The size reads ABOVE the table, axis-aligned rather than turned with it - it is
                a fact about the table, not a decoration on it, and stays legible upside down.
                Shown only while a resize handle is actually dragging, the same way the rotate
                angle only shows mid-turn. */
              }
              {overlay.resizing
                ? (() => {
                  const resizingTable = plan.tables.find((table) =>
                    table.id === overlay.resizing?.id
                  );
                  if (!resizingTable) return null;
                  const live = { ...resizingTable, ...overlay.resizing };
                  const top = Math.min(...tablePolygon(live).map((corner) => corner.y));
                  const label = `${formatLength(live.width)} × ${formatLength(live.depth)}`;
                  const width = stroke * (10 + label.length * 5.5);
                  const height = stroke * 13;
                  const centre = {
                    x: clamp(live.center.x, view.x + width / 2, view.x + view.width - width / 2),
                    y: clamp(
                      top - stroke * 14,
                      view.y + height / 2,
                      view.y + view.height - height / 2,
                    ),
                  };
                  return (
                    <G>
                      <Rect
                        x={centre.x - width / 2}
                        y={centre.y - height / 2}
                        width={width}
                        height={height}
                        rx={height / 2}
                        fill={colors.foreground}
                      />
                      <SvgText
                        x={centre.x}
                        y={centre.y + stroke * 4.5}
                        fill={colors.surface}
                        fontFamily="BricolageGrotesque_600SemiBold"
                        fontSize={stroke * 11}
                        textAnchor="middle"
                      >
                        {label}
                      </SvgText>
                    </G>
                  );
                })()
                : null}
              {
                /* The angle reads OFF the table, not on it: inside the footprint it competes
                with the number, which is the one figure that has to stay dominant. Carried on
                the ray out through the rotate control, so it clears the pointer too, and held
                inside the viewport so a table near the edge does not push it off screen. */
              }
              {overlay.rotating && overlay.rotateControl
                ? (() => {
                  const at = overlay.rotateControl.at;
                  const turned = plan.tables.find((table) => table.id === overlay.rotating?.id);
                  const away = {
                    x: at.x - (turned?.center.x ?? at.x),
                    y: at.y - (turned?.center.y ?? at.y),
                  };
                  const span = Math.hypot(away.x, away.y) || 1;
                  const reach = stroke * 12;
                  const label = `${Math.round(((overlay.rotating.rotation % 360) + 360) % 360)}°`;
                  const width = stroke * (6 + label.length * 5.5);
                  const height = stroke * 13;
                  const centre = {
                    x: clamp(
                      at.x + (away.x / span) * reach,
                      view.x + width / 2,
                      view.x + view.width - width / 2,
                    ),
                    y: clamp(
                      at.y + (away.y / span) * reach,
                      view.y + height / 2,
                      view.y + view.height - height / 2,
                    ),
                  };
                  return (
                    <G pointerEvents="none">
                      <Rect
                        x={centre.x - width / 2}
                        y={centre.y - height / 2}
                        width={width}
                        height={height}
                        rx={height / 2}
                        fill={colors.surface}
                        stroke={colors.primary}
                        strokeWidth={stroke}
                      />
                      <SvgText
                        x={centre.x}
                        y={centre.y + stroke * 3.4}
                        fill={colors.foreground}
                        fontFamily="BricolageGrotesque_600SemiBold"
                        fontSize={stroke * 9}
                        textAnchor="middle"
                      >
                        {label}
                      </SvgText>
                    </G>
                  );
                })()
                : null}
              {plan.tables
                .filter((table) => overlay.locked.includes(table.id))
                .map((table) => (
                  <Polygon
                    key={`pinned-${table.id}`}
                    points={points(tablePolygon(table))}
                    fill="none"
                    stroke={colors.mutedForeground}
                    strokeWidth={stroke * 1.5}
                    strokeDasharray={`${stroke * 3} ${stroke * 2}`}
                  />
                ))}
              {overlay.handles.map((handle, index) => (
                <Circle
                  key={`handle-${index}`}
                  cx={handle.x}
                  cy={handle.y}
                  r={stroke * 4}
                  fill={colors.surface}
                  stroke={colors.primary}
                  strokeWidth={stroke * 1.5}
                />
              ))}
              {overlay.snapPoint
                ? (
                  <Circle
                    testID="floor-plan-snap-point"
                    cx={overlay.snapPoint.x}
                    cy={overlay.snapPoint.y}
                    r={stroke * 2.5}
                    fill={colors.primary}
                  />
                )
                : null}
            </G>
          )
          : null}
      </Svg>
    </View>
  );
  return <GestureDetector gesture={gesture}>{canvas}</GestureDetector>;
}

/**
 * A Vendor the Organizer can hand tables to. Identity and request data live HERE rather than in
 * the plan: ADR-0069 keeps the document renderer-neutral and the backend drops fields it does not
 * know, so a vendor name stored in the layout would be both wrong and silently lost.
 */
export type FloorPlanVendor = {
  id: string;
  name: string;
  /** Tables their approved request calls for, or null when it names no number. */
  requested: number | null;
  /** Their logo, which becomes the badge on every table they hold. */
  image?: string;
};
